/**
 * Publicaciones oficiales firmadas como AiPeces Oficial (feed Comunidad, posición 1ª).
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Fish, Plus, ToggleLeft, ToggleRight, Trash2, Upload, Eye, Bell, BarChart3, Send,
} from 'lucide-react';
import { apiClient } from '@/services/api-client';
import { PageHeader } from '@/components/shared/PageHeader';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';

const OFFICIAL_USER_ID = 'f2a7a0e4-8f4a-4d8c-b77a-3c8b0c7a5b2d';
const OFFICIAL_DISPLAY_NAME = 'AiPeces Oficial';
const VALIDITY_OPTIONS = [1, 3, 5, 7, 15, 30, 90] as const;
const CDN_BASE = 'https://aipeces-media-a4hsbvf5gff3b6b0.z02.azurefd.net';
const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

interface OfficialPoll {
  id: string;
  question: string;
  totalVotes: number;
  options: Array<{ id: string; text: string; votes: number; percent: number }>;
}

interface OfficialPublication {
  id: string;
  momentId: string;
  adminTitle: string | null;
  caption: string;
  image: string | null;
  publicationType: 'media' | 'link';
  linkUrl: string | null;
  startsAt: string;
  endsAt: string;
  validityDays: number;
  isActive: boolean;
  isDraft?: boolean;
  maxImpressionsPerUser: number;
  sendPush: boolean;
  pushTitle: string | null;
  pushMessage: string | null;
  pushSentAt: string | null;
  createdAt: string;
  likesCount: number;
  commentsCount: number;
  impressionCount: number;
  uniqueUsers: number;
  poll: OfficialPoll | null;
}

interface CreateForm {
  caption: string;
  adminTitle: string;
  publicationType: 'media' | 'link';
  imageUrl: string;
  linkUrl: string;
  validityDays: number;
  maxImpressionsPerUser: number;
  sendPush: boolean;
  pushTitle: string;
  pushMessage: string;
  pollQuestion: string;
  pollOptions: string[];
  /** datetime-local value; vacío = ahora */
  startsAtLocal: string;
}

const EMPTY_FORM: CreateForm = {
  caption: '',
  adminTitle: '',
  publicationType: 'media',
  imageUrl: '',
  linkUrl: '',
  validityDays: 7,
  maxImpressionsPerUser: 3,
  sendPush: true,
  pushTitle: '',
  pushMessage: '',
  pollQuestion: '',
  pollOptions: ['', ''],
  startsAtLocal: '',
};

type SubmitMode = 'publish' | 'draft';

function mediaSrc(url: string | null): string | null {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${CDN_BASE}/${url.replace(/^\//, '')}`;
}

function isVideoUrl(url: string): boolean {
  return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url) || url.includes('video');
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function localToIso(local: string): string | null {
  if (!local.trim()) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function statusOf(row: OfficialPublication): { label: string; className: string } {
  if (row.isDraft) return { label: 'Borrador', className: 'bg-slate-500/20 text-slate-300' };
  if (!row.isActive) return { label: 'Inactiva', className: 'bg-gray-500/20 text-gray-400' };
  const now = Date.now();
  if (new Date(row.endsAt).getTime() < now) {
    return { label: 'Expirada', className: 'bg-red-500/20 text-red-400' };
  }
  if (new Date(row.startsAt).getTime() > now) {
    return { label: 'Programada', className: 'bg-yellow-500/20 text-yellow-400' };
  }
  return { label: 'Activa', className: 'bg-emerald-500/20 text-emerald-400' };
}

async function countPushRecipients(): Promise<number> {
  const seg = await apiClient.rpc<unknown>('admin_get_segmented_user_ids', {});
  if (seg.error) throw new Error(seg.error.message);
  const raw = Array.isArray(seg.data) ? seg.data : [];
  return raw.filter(Boolean).length;
}

async function broadcastPush(params: {
  title: string;
  message: string;
  imageUrl?: string;
  momentId: string;
}): Promise<{ sent: number; failed: number }> {
  const seg = await apiClient.rpc<unknown>('admin_get_segmented_user_ids', {});
  if (seg.error) throw new Error(seg.error.message);
  const raw = Array.isArray(seg.data) ? seg.data : [];
  const ids = raw.map((userId: unknown) =>
    typeof userId === 'object' && userId !== null && 'user_id' in userId
      ? String((userId as { user_id: string }).user_id)
      : String(userId)
  ).filter(Boolean);
  let sent = 0;
  let failed = 0;
  const BATCH = 10;
  for (let i = 0; i < ids.length; i += BATCH) {
    const batch = ids.slice(i, i + BATCH);
    const results = await Promise.allSettled(
      batch.map((userId) =>
        apiClient.post<{ sent?: number }>('/send-push-notification', {
          user_id: userId,
          title: params.title,
          message: params.message,
          type: 'new_moment',
          image_url: params.imageUrl || undefined,
          data: {
            moment_id: params.momentId,
            author_id: OFFICIAL_USER_ID,
            author_name: OFFICIAL_DISPLAY_NAME,
            official: true,
          },
        })
      )
    );
    for (const r of results) {
      if (r.status === 'fulfilled' && !r.value.error) sent += r.value.data?.sent ?? 0;
      else failed += 1;
    }
  }
  return { sent, failed };
}

export function PescadilloPostsPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CreateForm>({ ...EMPTY_FORM });
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [pushStatus, setPushStatus] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingMode, setPendingMode] = useState<SubmitMode | null>(null);
  const [confirmPublish, setConfirmPublish] = useState<{
    recipients: number;
    mode: SubmitMode;
  } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pushRowId, setPushRowId] = useState<string | null>(null);
  const [publishDraftId, setPublishDraftId] = useState<string | null>(null);
  const [publishDraftRecipients, setPublishDraftRecipients] = useState<number | null>(null);

  const { data: rows = [], isLoading } = useQuery<OfficialPublication[]>({
    queryKey: ['official-pescadillo-posts'],
    queryFn: async () => {
      const result = await apiClient.rpc<unknown>('admin_list_official_publications');
      if (result.error) throw new Error(result.error.message);
      let raw = result.data;
      if (typeof raw === 'string') {
        try {
          raw = JSON.parse(raw);
        } catch {
          return [];
        }
      }
      if (Array.isArray(raw)) return raw as OfficialPublication[];
      return [];
    },
  });

  const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const stats = useMemo(() => {
    const active = list.filter(
      (r) => !r.isDraft && r.isActive && new Date(r.endsAt).getTime() > Date.now()
        && new Date(r.startsAt).getTime() <= Date.now(),
    ).length;
    const impressions = list.reduce((s, r) => s + (r.impressionCount || 0), 0);
    const pushes = list.filter((r) => r.pushSentAt).length;
    const drafts = list.filter((r) => r.isDraft).length;
    return { total: list.length, active, impressions, pushes, drafts };
  }, [list]);

  const parseCreated = (resultData: unknown) => {
    let created = Array.isArray(resultData) ? resultData[0] : resultData;
    if (typeof created === 'string') created = JSON.parse(created);
    return created as {
      publicationId: string;
      momentId: string;
      sendPush: boolean;
      pushTitle: string;
      pushMessage: string;
      startsAt?: string;
      isDraft?: boolean;
    } | null;
  };

  const createMutation = useMutation({
    mutationFn: async (mode: SubmitMode) => {
      const pollOpts = form.pollOptions.map((o) => o.trim()).filter(Boolean);
      const startsIso = localToIso(form.startsAtLocal);
      const params: Record<string, unknown> = {
        p_caption: form.caption.trim(),
        p_image: form.imageUrl.trim() || null,
        p_publication_type: form.publicationType,
        p_link_url: form.publicationType === 'link' ? form.linkUrl.trim() : null,
        p_validity_days: form.validityDays,
        p_max_impressions_per_user: form.maxImpressionsPerUser,
        p_send_push: form.sendPush,
        p_push_title: form.pushTitle.trim() || null,
        p_push_message: form.pushMessage.trim() || null,
        p_admin_title: form.adminTitle.trim() || null,
        p_is_draft: mode === 'draft',
        p_starts_at: mode === 'draft' ? null : startsIso,
      };
      if (form.pollQuestion.trim()) {
        params.p_poll_question = form.pollQuestion.trim();
        params.p_poll_options = pollOpts;
      }
      const result = await apiClient.rpc<unknown>('admin_create_official_publication', params);
      if (result.error) throw new Error(result.error.message);
      const payload = parseCreated(result.data);
      if (!payload?.publicationId) throw new Error('Respuesta inválida al crear publicación');

      if (payload.sendPush && !payload.isDraft) {
        setPushStatus('Enviando push a todos los usuarios…');
        const push = await broadcastPush({
          title: payload.pushTitle || 'Novedad de AiPeces',
          message: payload.pushMessage || form.caption.trim().slice(0, 140),
          imageUrl: form.imageUrl.trim() || undefined,
          momentId: payload.momentId,
        });
        await apiClient.rpc('admin_mark_official_publication_push_sent', { p_id: payload.publicationId });
        setPushStatus(`Push: ${push.sent} enviados, ${push.failed} fallidos`);
      } else if (form.sendPush && startsIso && new Date(startsIso).getTime() > Date.now()) {
        setPushStatus('Publicación programada: el push no se envía hasta que esté activa (usa «Enviar push» en la lista).');
      }
      return payload;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['official-pescadillo-posts'] });
      setDialogOpen(false);
      setForm({ ...EMPTY_FORM });
      setFormError(null);
      setPendingMode(null);
      setConfirmPublish(null);
    },
    onError: (err: Error) => {
      setFormError(err.message || 'Error al publicar');
      setPushStatus(null);
      setPendingMode(null);
    },
  });

  const publishDraftMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await apiClient.rpc<unknown>('admin_publish_official_publication', { p_id: id });
      if (result.error) throw new Error(result.error.message);
      const payload = parseCreated(result.data);
      if (!payload?.publicationId) throw new Error('Respuesta inválida al publicar borrador');
      if (payload.sendPush) {
        const row = list.find((r) => r.id === id);
        setPushStatus('Enviando push…');
        const push = await broadcastPush({
          title: payload.pushTitle || 'Novedad de AiPeces',
          message: payload.pushMessage || row?.caption?.slice(0, 140) || '',
          imageUrl: row?.image || undefined,
          momentId: payload.momentId,
        });
        await apiClient.rpc('admin_mark_official_publication_push_sent', { p_id: payload.publicationId });
        setPushStatus(`Push: ${push.sent} enviados, ${push.failed} fallidos`);
      }
      return payload;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['official-pescadillo-posts'] }),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const result = await apiClient.rpc('admin_toggle_official_publication', {
        p_id: id,
        p_is_active: isActive,
      });
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['official-pescadillo-posts'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await apiClient.rpc('admin_delete_official_publication', { p_id: id });
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['official-pescadillo-posts'] });
      setDeleteId(null);
    },
  });

  const sendPushMutation = useMutation({
    mutationFn: async (row: OfficialPublication) => {
      const push = await broadcastPush({
        title: row.pushTitle || 'Novedad de AiPeces',
        message: row.pushMessage || row.caption.slice(0, 140),
        imageUrl: row.image || undefined,
        momentId: row.momentId,
      });
      await apiClient.rpc('admin_mark_official_publication_push_sent', { p_id: row.id });
      return push;
    },
    onSuccess: () => {
      setPushRowId(null);
      queryClient.invalidateQueries({ queryKey: ['official-pescadillo-posts'] });
    },
  });

  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormError(null);

    const isVideo = file.type.startsWith('video/') || /\.(mp4|webm|mov|m4v)$/i.test(file.name);
    const max = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
    if (file.size > max) {
      setFormError(
        `El archivo pesa ${formatBytes(file.size)}. Máximo ${isVideo ? 'vídeo 100 MB' : 'imagen 15 MB'}.`,
      );
      e.target.value = '';
      return;
    }

    setUploading(true);
    setUploadPct(0);
    try {
      const ext = file.name.split('.').pop() || (isVideo ? 'mp4' : 'jpg');
      const path = `${OFFICIAL_USER_ID}/moments/${OFFICIAL_USER_ID}-${Date.now()}.${ext}`;
      // SAS directo a Blob: evita base64 (colgaba en vídeos ~9 MB+).
      const result = await apiClient.uploadViaSas('capture-media', path, file, {
        contentType: file.type || (isVideo ? 'video/mp4' : 'image/jpeg'),
        onProgress: setUploadPct,
      });
      if (result.error) throw new Error(result.error.message);
      const url =
        result.data?.publicUrl ||
        result.data?.url ||
        `${CDN_BASE}/capture-media/${path}`;
      setForm((f) => ({ ...f, imageUrl: url, publicationType: 'media' }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Error al subir archivo');
    } finally {
      setUploading(false);
      setUploadPct(null);
      e.target.value = '';
    }
  }, []);

  const validateForm = (): string | null => {
    if (!form.caption.trim()) return 'El texto es obligatorio';
    if (form.publicationType === 'media' && !form.imageUrl.trim()) return 'Sube una imagen o vídeo';
    if (form.publicationType === 'link' && !form.linkUrl.trim()) return 'Indica la URL del enlace';
    if (form.pollQuestion.trim()) {
      const opts = form.pollOptions.map((o) => o.trim()).filter(Boolean);
      if (opts.length < 2) return 'La encuesta necesita al menos 2 opciones';
    }
    if (form.startsAtLocal && !localToIso(form.startsAtLocal)) {
      return 'Fecha de publicación no válida';
    }
    return null;
  };

  const requestSubmit = async (mode: SubmitMode) => {
    setFormError(null);
    setPushStatus(null);
    const err = validateForm();
    if (err) {
      setFormError(err);
      return;
    }

    const willPushNow =
      mode === 'publish'
      && form.sendPush
      && (!form.startsAtLocal || (localToIso(form.startsAtLocal) && new Date(localToIso(form.startsAtLocal)!).getTime() <= Date.now()));

    if (willPushNow) {
      setConfirmLoading(true);
      setPendingMode(mode);
      try {
        const recipients = await countPushRecipients();
        setConfirmPublish({ recipients, mode });
      } catch (e) {
        setFormError(e instanceof Error ? e.message : 'No se pudo calcular destinatarios del push');
        setPendingMode(null);
      } finally {
        setConfirmLoading(false);
      }
      return;
    }

    setPendingMode(mode);
    createMutation.mutate(mode);
  };

  const previewSrc = mediaSrc(form.imageUrl.trim() || null);

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Publicaciones AiPeces Oficial"
        description="Noticias e iniciativas oficiales en 1ª posición del feed Comunidad"
      >
        <button
          type="button"
          onClick={() => {
            setForm({ ...EMPTY_FORM });
            setFormError(null);
            setPushStatus(null);
            setDialogOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90"
        >
          <Plus size={16} />
          Nueva publicación
        </button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Metric icon={<Fish size={18} />} label="Total" value={stats.total} />
        <Metric icon={<ToggleRight size={18} />} label="Activas" value={stats.active} />
        <Metric icon={<Eye size={18} />} label="Impresiones boost" value={stats.impressions} />
        <Metric icon={<Bell size={18} />} label="Con push enviado" value={stats.pushes} />
        <Metric icon={<Fish size={18} />} label="Borradores" value={stats.drafts} />
      </div>

      {pushStatus && !dialogOpen && (
        <p className="text-sm text-muted-foreground">{pushStatus}</p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl">
          <Fish className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Sin publicaciones oficiales</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Publica la primera novedad firmada por AiPeces Oficial
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-muted-foreground">
              <tr>
                <th className="text-left p-3 font-medium">Publicación</th>
                <th className="text-left p-3 font-medium">Estado</th>
                <th className="text-left p-3 font-medium">Vigencia</th>
                <th className="text-left p-3 font-medium">Visibilidad</th>
                <th className="text-left p-3 font-medium">Stats</th>
                <th className="text-right p-3 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => {
                const st = statusOf(row);
                const img = mediaSrc(row.image);
                const canPush =
                  !row.isDraft
                  && row.sendPush
                  && !row.pushSentAt
                  && row.isActive
                  && new Date(row.startsAt).getTime() <= Date.now();
                return (
                  <tr key={row.id} className="border-b border-border/60 last:border-0">
                    <td className="p-3">
                      <div className="flex gap-3 items-start max-w-md">
                        {img ? (
                          isVideoUrl(img) ? (
                            <video src={img} className="w-14 h-14 rounded-lg object-cover bg-muted shrink-0" muted />
                          ) : (
                            <img src={img} alt="" className="w-14 h-14 rounded-lg object-cover bg-muted shrink-0" />
                          )
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-muted shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-foreground line-clamp-2">{row.caption}</p>
                          {row.poll && (
                            <p className="text-xs text-primary mt-1">Encuesta · {row.poll.totalVotes} votos</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${st.className}`}>
                        {st.label}
                      </span>
                      {row.pushSentAt && (
                        <p className="text-[11px] text-muted-foreground mt-1">Push enviado</p>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground whitespace-nowrap">
                      {row.validityDays}d · desde {new Date(row.startsAt).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                      <br />
                      hasta {new Date(row.endsAt).toLocaleDateString('es-ES')}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      Máx. {row.maxImpressionsPerUser} vez{row.maxImpressionsPerUser === 1 ? '' : 'es'}/usuario
                    </td>
                    <td className="p-3 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <BarChart3 size={14} />
                        {row.impressionCount} · {row.likesCount}❤ · {row.commentsCount}💬
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        {row.isDraft && (
                          <button
                            type="button"
                            title="Publicar borrador"
                            onClick={() => {
                              void (async () => {
                                setPublishDraftId(row.id);
                                if (row.sendPush) {
                                  try {
                                    setPublishDraftRecipients(await countPushRecipients());
                                  } catch {
                                    setPublishDraftRecipients(null);
                                  }
                                } else {
                                  setPublishDraftRecipients(null);
                                }
                              })();
                            }}
                            disabled={publishDraftMutation.isPending}
                            className="p-2 rounded-lg hover:bg-muted text-primary text-xs font-medium"
                          >
                            Publicar
                          </button>
                        )}
                        {canPush && (
                          <button
                            type="button"
                            title="Enviar push pendiente"
                            onClick={() => setPushRowId(row.id)}
                            className="p-2 rounded-lg hover:bg-muted text-muted-foreground"
                          >
                            <Send size={16} className="text-amber-400" />
                          </button>
                        )}
                        {!row.isDraft && (
                          <button
                            type="button"
                            title={row.isActive ? 'Desactivar boost' : 'Activar boost'}
                            onClick={() => toggleMutation.mutate({ id: row.id, isActive: !row.isActive })}
                            className="p-2 rounded-lg hover:bg-muted text-muted-foreground"
                          >
                            {row.isActive ? <ToggleRight size={18} className="text-emerald-400" /> : <ToggleLeft size={18} />}
                          </button>
                        )}
                        <button
                          type="button"
                          title="Retirar"
                          onClick={() => setDeleteId(row.id)}
                          className="p-2 rounded-lg hover:bg-muted text-red-400"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-card p-5">
            <h2 className="text-lg font-bold mb-4">Nueva publicación AiPeces Oficial</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void requestSubmit('publish');
                }}
                className="space-y-4"
              >
                <Field label="Título interno (opcional)">
                  <input
                    className="input"
                    value={form.adminTitle}
                    onChange={(e) => setForm((f) => ({ ...f, adminTitle: e.target.value }))}
                    placeholder="Ej. Lanzamiento Liga"
                  />
                </Field>

                <Field label="Texto *">
                  <textarea
                    className="input min-h-[88px]"
                    value={form.caption}
                    onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))}
                    required
                  />
                </Field>

                <div className="flex gap-2">
                  {(['media', 'link'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, publicationType: t }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                        form.publicationType === t
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {t === 'media' ? 'Imagen / Vídeo' : 'Enlace'}
                    </button>
                  ))}
                </div>

                {form.publicationType === 'media' ? (
                  <Field label="Media">
                    <p className="text-[11px] text-muted-foreground mb-2">
                      Imagen máx. 15 MB · Vídeo máx. 100 MB (recomendado ≤720p / ~10 MB para el feed).
                    </p>
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm cursor-pointer">
                      <Upload size={14} />
                      {uploading
                        ? `Subiendo…${uploadPct != null ? ` ${uploadPct}%` : ''}`
                        : 'Subir archivo'}
                      <input
                        type="file"
                        accept="image/*,video/mp4,video/webm,video/quicktime"
                        className="hidden"
                        disabled={uploading}
                        onChange={handleUpload}
                      />
                    </label>
                    {uploading && uploadPct != null && (
                      <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${uploadPct}%` }}
                        />
                      </div>
                    )}
                    {form.imageUrl && (
                      <p className="text-xs text-muted-foreground mt-2 break-all">{form.imageUrl}</p>
                    )}
                  </Field>
                ) : (
                  <Field label="URL del enlace *">
                    <input
                      className="input"
                      value={form.linkUrl}
                      onChange={(e) => setForm((f) => ({ ...f, linkUrl: e.target.value }))}
                      placeholder="https://"
                    />
                  </Field>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Vigencia (días)">
                    <select
                      className="input"
                      value={form.validityDays}
                      onChange={(e) => setForm((f) => ({ ...f, validityDays: Number(e.target.value) }))}
                    >
                      {VALIDITY_OPTIONS.map((d) => (
                        <option key={d} value={d}>{d} días</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Mostrar N veces / usuario">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      className="input"
                      value={form.maxImpressionsPerUser}
                      onChange={(e) => setForm((f) => ({ ...f, maxImpressionsPerUser: Number(e.target.value) || 1 }))}
                    />
                  </Field>
                </div>

                <Field label="Programar publicación (opcional)">
                  <input
                    type="datetime-local"
                    className="input"
                    value={form.startsAtLocal}
                    onChange={(e) => setForm((f) => ({ ...f, startsAtLocal: e.target.value }))}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Vacío = publicar ahora. Si es futura, el boost arranca en esa fecha.
                  </p>
                </Field>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.sendPush}
                    onChange={(e) => setForm((f) => ({ ...f, sendPush: e.target.checked }))}
                  />
                  Enviar push automático al publicar (recomendado)
                </label>

                {form.sendPush && (
                  <div className="space-y-3 rounded-lg border border-border p-3">
                    <Field label="Título push">
                      <input
                        className="input"
                        value={form.pushTitle}
                        onChange={(e) => setForm((f) => ({ ...f, pushTitle: e.target.value }))}
                        placeholder="Novedad de AiPeces"
                      />
                    </Field>
                    <Field label="Mensaje push">
                      <input
                        className="input"
                        value={form.pushMessage}
                        onChange={(e) => setForm((f) => ({ ...f, pushMessage: e.target.value }))}
                        placeholder="Se usa el texto de la publicación si lo dejas vacío"
                      />
                    </Field>
                  </div>
                )}

                <div className="space-y-2 rounded-lg border border-dashed border-border p-3">
                  <p className="text-sm font-medium">Encuesta (opcional)</p>
                  <input
                    className="input"
                    value={form.pollQuestion}
                    onChange={(e) => setForm((f) => ({ ...f, pollQuestion: e.target.value }))}
                    placeholder="Pregunta"
                  />
                  {form.pollOptions.map((opt, idx) => (
                    <input
                      key={idx}
                      className="input"
                      value={opt}
                      onChange={(e) => {
                        const next = [...form.pollOptions];
                        next[idx] = e.target.value;
                        setForm((f) => ({ ...f, pollOptions: next }));
                      }}
                      placeholder={`Opción ${idx + 1}`}
                    />
                  ))}
                  {form.pollOptions.length < 6 && (
                    <button
                      type="button"
                      className="text-xs text-primary"
                      onClick={() => setForm((f) => ({ ...f, pollOptions: [...f.pollOptions, ''] }))}
                    >
                      + Opción
                    </button>
                  )}
                </div>

                {formError && <p className="text-sm text-red-400">{formError}</p>}
                {pushStatus && <p className="text-sm text-muted-foreground">{pushStatus}</p>}

                <div className="flex flex-wrap justify-end gap-2 pt-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg text-sm bg-muted"
                    onClick={() => setDialogOpen(false)}
                    disabled={createMutation.isPending || uploading || confirmLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg text-sm border border-border"
                    disabled={createMutation.isPending || uploading || confirmLoading}
                    onClick={() => void requestSubmit('draft')}
                  >
                    {createMutation.isPending && pendingMode === 'draft' ? 'Guardando…' : 'Guardar borrador'}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50"
                    disabled={createMutation.isPending || uploading || confirmLoading}
                  >
                    {confirmLoading
                      ? 'Comprobando…'
                      : createMutation.isPending && pendingMode === 'publish'
                        ? 'Publicando…'
                        : 'Publicar'}
                  </button>
                </div>
              </form>

              <div className="rounded-xl border border-border bg-background/60 p-4 space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Vista previa en la app
                </p>
                <div className="rounded-2xl border border-border/80 bg-card overflow-hidden shadow-sm max-w-sm mx-auto">
                  <div className="flex items-center gap-2 p-3 border-b border-border/50">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                      AO
                    </div>
                    <div>
                      <p className="text-sm font-semibold leading-tight">{OFFICIAL_DISPLAY_NAME}</p>
                      <p className="text-[10px] text-muted-foreground">Publicación oficial</p>
                    </div>
                  </div>
                  {previewSrc ? (
                    isVideoUrl(previewSrc) ? (
                      <video src={previewSrc} controls className="w-full aspect-[9/16] max-h-72 object-cover bg-black" />
                    ) : (
                      <img src={previewSrc} alt="" className="w-full aspect-square object-cover bg-muted" />
                    )
                  ) : form.publicationType === 'link' && form.linkUrl ? (
                    <div className="p-4 text-sm text-primary break-all">{form.linkUrl}</div>
                  ) : (
                    <div className="aspect-square bg-muted flex items-center justify-center text-xs text-muted-foreground">
                      Sin media
                    </div>
                  )}
                  <div className="p-3">
                    <p className="text-sm whitespace-pre-wrap">
                      {form.caption.trim() || <span className="text-muted-foreground">Texto de la publicación…</span>}
                    </p>
                    {form.pollQuestion.trim() && (
                      <div className="mt-3 rounded-lg border border-border p-2 space-y-1">
                        <p className="text-xs font-medium">{form.pollQuestion}</p>
                        {form.pollOptions.filter((o) => o.trim()).map((o, i) => (
                          <div key={i} className="text-[11px] px-2 py-1 rounded bg-muted/60">{o}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminConfirmDialog
        open={!!confirmPublish}
        title="¿Enviar push a todos?"
        description={
          confirmPublish
            ? `Se publicará como ${OFFICIAL_DISPLAY_NAME} y se notificará a ${confirmPublish.recipients.toLocaleString('es-ES')} usuarios.`
            : undefined
        }
        confirmLabel="Publicar y notificar"
        variant="danger"
        affectedCount={confirmPublish?.recipients}
        affectedNoun="usuarios"
        confirmDisabled={createMutation.isPending}
        onCancel={() => {
          setConfirmPublish(null);
          setPendingMode(null);
        }}
        onConfirm={() => {
          if (!confirmPublish) return;
          createMutation.mutate(confirmPublish.mode);
        }}
      />

      <AdminConfirmDialog
        open={!!publishDraftId}
        title="Publicar borrador"
        description={
          publishDraftRecipients != null
            ? `Se publicará como ${OFFICIAL_DISPLAY_NAME} y se enviará push a ${publishDraftRecipients.toLocaleString('es-ES')} usuarios.`
            : `Se publicará como ${OFFICIAL_DISPLAY_NAME} en el feed Comunidad.`
        }
        confirmLabel={publishDraftRecipients != null ? 'Publicar y notificar' : 'Publicar'}
        affectedCount={publishDraftRecipients ?? undefined}
        affectedNoun="usuarios"
        confirmDisabled={publishDraftMutation.isPending}
        onCancel={() => {
          setPublishDraftId(null);
          setPublishDraftRecipients(null);
        }}
        onConfirm={() => {
          if (!publishDraftId) return;
          publishDraftMutation.mutate(publishDraftId, {
            onSettled: () => {
              setPublishDraftId(null);
              setPublishDraftRecipients(null);
            },
          });
        }}
      />

      <AdminConfirmDialog
        open={!!deleteId}
        title="Retirar publicación"
        description="Se desactiva el boost y se oculta del feed público. El historial queda en el perfil de AiPeces Oficial como no público."
        confirmLabel="Retirar"
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
      />

      <AdminConfirmDialog
        open={!!pushRowId}
        title="Enviar push pendiente"
        description="Se enviará la notificación push de esta publicación a todos los usuarios segmentados."
        confirmLabel="Enviar push"
        confirmDisabled={sendPushMutation.isPending}
        onCancel={() => setPushRowId(null)}
        onConfirm={() => {
          const row = list.find((r) => r.id === pushRowId);
          if (row) sendPushMutation.mutate(row);
        }}
      />

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.5rem;
          border: 1px solid hsl(var(--border));
          background: hsl(var(--background));
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          color: hsl(var(--foreground));
        }
      `}</style>
    </div>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: number | string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
        {icon}
        {label}
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
